import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// import Marker from "./Marker";
import {
  AdvancedMarker,
  APIProvider,
  ControlPosition,
  Map,
  Pin,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import CustomMapControl from "./MapControl";
import MapHandler from "./MapHandler";
import "./styles/map.css";

// Google Maps API key
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// CustomMapControl component for the map control
const MarkerAddressHandler = ({
  slide9,
  setSlide9,
  position,
  debouncedSave,
  setIsLoading,
}) => {
  const geocodeLib = useMapsLibrary("geocoding");
  const geocoder = useMemo(
    () => geocodeLib && new geocodeLib.Geocoder(),
    [geocodeLib],
  );

  const lastPositionRef = useRef(null);

  useEffect(() => {
    if (!geocoder || !position.lat || !position.lng) return;

    const currLat = Number(position.lat);
    const currLng = Number(position.lng);

    const hasPositionChanged =
      !lastPositionRef.current ||
      lastPositionRef.current.lat !== currLat ||
      lastPositionRef.current.lng !== currLng;

    if (hasPositionChanged) {
      lastPositionRef.current = { lat: currLat, lng: currLng };
      geocoder.geocode(
        {
          location: { lat: position.lat, lng: position.lng },
          fulfillOnZeroResults: true,
          region: "us",
        },
        (results, status) => {
          if (status === "OK") {
            let city = "";
            let county = "";
            let address = "";
            let zip = "";

            let dataArr = results;

            for (let i = 0; i < dataArr.length; i++) {
              if (city && county && address && zip) break;

              const data = dataArr[i];

              if (
                (data.geometry.location_type === "ROOFTOP" ||
                  data.geometry.location_type === "GEOMETRIC_CENTER") &&
                data.formatted_address.split(", ").length === 4
              ) {
                address =
                  data.formatted_address.split(", ")[0] !== "Unnamed Road"
                    ? data.formatted_address.split(", ")[0]
                    : "";
                city = data.formatted_address.split(", ")[1];
                zip = data.formatted_address.split(", ")[2].split("NV ")[1];
              }

              for (let j = 0; j < data.address_components.length; j++) {
                if (city && county && address && zip) break;

                let arr2 = data.address_components[j];

                if (arr2.types.includes("point_of_interest")) {
                  address = arr2.short_name;
                  continue;
                }

                if (arr2.types.includes("locality") && !city) {
                  city = arr2.short_name;
                  continue;
                }

                if (
                  arr2.types.includes("administrative_area_level_2") &&
                  !county
                ) {
                  county = arr2.short_name.split(" County")[0];
                  continue;
                }

                if (arr2.types.includes("postal_code") && !zip) {
                  zip = arr2.short_name;
                  continue;
                }
              }
            }

            if (address.includes(" FS ")) {
              address = address.replace(" FS ", " & ");
            }

            if (address.includes(" Av ")) {
              address = address.replace(" Av ", " Ave ");
            }

            let newState = {
              ...slide9,
              pAdd: address,
              pCity: city,
              pCounty: county,
              pZip: zip,
            };

            setSlide9(newState);

            if (debouncedSave) {
              setIsLoading(true);
              debouncedSave.mutate(newState);
            }
          }
        },
      );
    }
  }, [position]);

  return null;
};

// MapComponent component for the map
const MapComponent = ({
  projectName,
  slide9,
  setSlide9,
  requestStatus,
  disableAll,
  debouncedSave,
  setIsLoading,
}) => {
  const { projectId } = useContext(DashboardContext);

  const [selectedPlace, setSelectedPlace] = useState(null);

  // Handles the map click event
  const handleMapClick = useCallback((e) => {
    let latStr = e.detail.latLng.lat.toString().slice(0, 11);
    let lngStr = e.detail.latLng.lng.toString().slice(0, 12);
    if (slide9.isLinear !== "Yes") {
      // Should geocode for the address after marker.position is set
      setSlide9({
        ...slide9,
        bLat: latStr,
        bLng: lngStr,
        marker: {
          position: [e.detail.latLng.lat, e.detail.latLng.lng],
          details: ["", "", "", ""],
        },
      });
    }

    if (slide9.isLinear == "Yes" && !slide9.bLat && !slide9.bLng) {
      // Should geocode for the address after marker.position is set
      setSlide9({
        ...slide9,
        bLat: latStr,
        bLng: lngStr,
        marker: {
          position: [e.detail.latLng.lat, e.detail.latLng.lng],
          details: ["", "", ""],
        },
      });
    } else if (slide9.isLinear == "Yes" && slide9.bLat && slide9.bLng) {
      // Should NOT geocode for the address after marker2.position is set
      let newState = {
        ...slide9,
        eLat: latStr,
        eLng: lngStr,
        marker2: {
          position: [e.detail.latLng.lat, e.detail.latLng.lng],
          details: ["", "", "", ""],
        },
      };
      setSlide9(newState);

      if (debouncedSave) {
        setIsLoading(true);
        debouncedSave.mutate(newState);
      }
    }
  });

  return (
    <div
      className={`map-container`}
      style={{
        pointerEvents:
          (requestStatus !== "I" && requestStatus !== "IP") || disableAll
            ? "none"
            : "auto",
      }}
    >
      <APIProvider
        apiKey={API_KEY}
        region="us"
        language="en"
        libraries={["places", "geocoding", "geometry", "routes"]}
      >
        <Map
          defaultZoom={10}
          defaultCenter={{ lat: 36.161973, lng: -115.16355 }}
          reuseMap={true}
          mapId={`${projectId}-map`}
          mapTypeControl={false}
          mapTypeId="hybrid"
          streetViewControl={false}
          onClick={handleMapClick}
          rotateControl={false}
          className="new-map"
        >
          <CustomMapControl
            slide9={slide9}
            controlPosition={ControlPosition.LEFT_TOP}
            onPlaceSelect={setSelectedPlace}
          />
          <MapHandler place={selectedPlace} setSlide9={setSlide9} />
          <AdvancedMarker
            style={{
              pointerEvents:
                (requestStatus !== "I" && requestStatus !== "IP") || disableAll
                  ? "none"
                  : "auto",
              cursor:
                (requestStatus !== "I" && requestStatus !== "IP") || disableAll
                  ? "not-allowed"
                  : "pointer",
            }}
            position={{
              lat: slide9.marker.position[0] ? +slide9.marker.position[0] : 0,
              lng: slide9.marker.position[1] ? +slide9.marker.position[1] : 0,
            }}
            onClick={(e) => {
              let newState = {
                ...slide9,
                bLat: "",
                bLng: "",
                eLat: "",
                eLng: "",
                marker: {
                  position: [0, 0],
                  details: ["", "", "", ""],
                },
                marker2: {
                  position: [0, 0],
                  details: ["", "", "", ""],
                },
              };
              setSlide9(newState);

              if (debouncedSave) {
                setIsLoading(true);
                debouncedSave.mutate(newState);
              }
            }}
            title={slide9.isLinear ? "Start" : projectName}
          >
            <Pin
              background="#00e30f"
              borderColor={"#08460f"}
              glyphColor={"#0a8316"}
              style={{
                pointerEvents:
                  (requestStatus !== "I" && requestStatus !== "IP") ||
                  disableAll
                    ? "none"
                    : "auto",
                cursor:
                  (requestStatus !== "I" && requestStatus !== "IP") ||
                  disableAll
                    ? "not-allowed"
                    : "pointer",
              }}
            />
          </AdvancedMarker>
          {slide9.isLinear === "Yes" && (
            <AdvancedMarker
              style={{
                pointerEvents:
                  (requestStatus !== "I" && requestStatus !== "IP") ||
                  disableAll
                    ? "none"
                    : "auto",
                cursor:
                  (requestStatus !== "I" && requestStatus !== "IP") ||
                  disableAll
                    ? "not-allowed"
                    : "pointer",
              }}
              position={{
                lat: slide9.marker2.position[0]
                  ? +slide9.marker2.position[0]
                  : 0,
                lng: slide9.marker2.position[1]
                  ? +slide9.marker2.position[1]
                  : 0,
              }}
              onClick={(e) => {
                let newState = {
                  ...slide9,
                  eLat: "",
                  eLng: "",
                  marker2: {
                    position: ["", ""],
                    details: ["", "", "", ""],
                  },
                };
                setSlide9(newState);

                if (debouncedSave) {
                  setIsLoading(true);
                  debouncedSave.mutate(newState);
                }
              }}
              title="End"
            >
              <Pin
                background="#ff0000"
                borderColor={"#000000"}
                glyphColor={"#a30000"}
                style={{
                  pointerEvents:
                    (requestStatus !== "I" && requestStatus !== "IP") ||
                    disableAll
                      ? "none"
                      : "auto",
                  cursor:
                    (requestStatus !== "I" && requestStatus !== "IP") ||
                    disableAll
                      ? "not-allowed"
                      : "pointer",
                }}
              />
            </AdvancedMarker>
          )}
          {slide9.marker.position[0] && slide9.marker.position[1] && (
            <MarkerAddressHandler
              slide9={slide9}
              setSlide9={setSlide9}
              position={{
                lat: slide9.marker.position[0],
                lng: slide9.marker.position[1],
              }}
              debouncedSave={debouncedSave}
              setIsLoading={setIsLoading}
            />
          )}
        </Map>
      </APIProvider>
    </div>
  );
};

export default MapComponent;
